import { alpha, Box, Paper, Stack, Typography } from '@mui/material';
import { CHECKOUT_COPY } from 'lib/billing';
import { formatCents } from '../format';
import { NotePanel } from '../NotePanel';
import { PAYMENT_TAB, useCheckoutState, useCheckoutStore } from '../store';

const COPY = CHECKOUT_COPY.payment.overdue;

// Overdue invoice summary — mirrors the plan checkout's "due today" block so
// the invoice-pay step reads consistently with the rest of the drawer.
export function OverdueAmountSummary() {
  const state = useCheckoutState() as any;
  const tab = useCheckoutStore((s) => s.paymentTab);
  const overdue = state.overdueInvoice;
  const availableCredit = Number(state.billingOverview?.availableCredit || 0);
  if (!overdue) return null;

  const due = formatCents(overdue.amountDueCents, overdue.currency);
  const balance = formatCents(availableCredit, overdue.currency);
  // A card pays the whole invoice, so say where the balance goes instead of letting it read as used.
  let cardNote = '';
  if (tab === PAYMENT_TAB.CARD && availableCredit > 0) {
    cardNote = availableCredit >= overdue.amountDueCents ? COPY.cardBalanceCovers(balance) : COPY.cardKeepsBalance(due, balance);
  }

  const summary = (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper' }}>
      <Box
        sx={(theme) => ({
          m: 1.25,
          p: 1.75,
          borderRadius: 2,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.07)} 0%, ${alpha(
            theme.palette.primary.main,
            0.02
          )} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`
        })}
      >
        <Stack spacing={1}>
          <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1.5}>
            <Typography
              variant="caption"
              sx={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: 'text.secondary' }}
            >
              {COPY.amountDueLabel}
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 800, fontVariantNumeric: 'tabular-nums', lineHeight: 1, letterSpacing: -0.4 }}>
              {due}
            </Typography>
          </Stack>
          {availableCredit > 0 && (
            <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1.5}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
                {COPY.balanceLabel}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                {balance}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Box>
    </Paper>
  );

  return (
    <>
      {summary}
      {/* Next to the Pay button, so it is read before paying. */}
      {cardNote && <NotePanel severity="info" title={COPY.cardNoteTitle} body={cardNote} />}
    </>
  );
}
